import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Save, Trash2, Eye, EyeOff, ExternalLink, Info, CheckCircle, AlertCircle, MapPin
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GoogleMapsSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showKey, setShowKey] = useState(false);
  const [settings, setSettings] = useState({
    api_key: '',
    enabled: false
  });
  const [currentSettings, setCurrentSettings] = useState({
    configured: false,
    api_key_masked: '',
    enabled: false
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/platform-settings/google-maps`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCurrentSettings(response.data);
      setSettings({
        api_key: '',
        enabled: response.data.enabled || false
      });
    } catch (error) {
      console.error('Failed to load Google Maps settings:', error);
      // Settings might not exist yet - that's OK
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!currentSettings.configured && !settings.api_key) {
      toast.error('Please enter a Google Maps API Key');
      return;
    }

    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      
      const payload = {
        api_key: settings.api_key || 'KEEP_EXISTING',
        enabled: settings.enabled
      };

      await axios.post(`${API_URL}/api/platform-settings/google-maps`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Google Maps settings saved successfully');
      loadSettings();
      setSettings(prev => ({ ...prev, api_key: '' }));
    } catch (error) {
      console.error('Failed to save Google Maps settings:', error);
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete the Google Maps configuration? Address autocomplete will be disabled.')) {
      return;
    }

    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/platform-settings/google-maps`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Google Maps settings deleted');
      loadSettings();
    } catch (error) {
      console.error('Failed to delete Google Maps settings:', error);
      toast.error('Failed to delete settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <MapPin className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <CardTitle>Google Maps API Configuration</CardTitle>
              <CardDescription>Platform-level Google Maps settings for address autocomplete</CardDescription>
            </div>
          </div>
          {currentSettings.configured ? (
            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
              <CheckCircle className="h-3 w-3 mr-1" />
              Configured
            </Badge>
          ) : (
            <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
              <AlertCircle className="h-3 w-3 mr-1" />
              Not Configured
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Info Alert */}
        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>
            Configure your Google Maps API key to enable address autocomplete across the platform.
            This key will be used for all clinics unless they configure their own.
            <a 
              href="https://console.cloud.google.com/google/maps-apis/credentials" 
              target="_blank" 
              rel="noopener noreferrer"
              className="ml-2 text-teal-600 hover:underline inline-flex items-center"
            >
              Google Cloud Console <ExternalLink className="h-3 w-3 ml-1" />
            </a>
          </AlertDescription>
        </Alert>

        {/* Enable/Disable Toggle */}
        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
          <div>
            <Label className="text-base font-medium">Enable Google Maps</Label>
            <p className="text-sm text-gray-500">Enable address autocomplete using Google Places API</p>
          </div>
          <Switch
            checked={settings.enabled}
            onCheckedChange={(checked) => setSettings(prev => ({ ...prev, enabled: checked }))}
          />
        </div>

        {/* API Key */}
        <div className="space-y-2">
          <Label htmlFor="api_key">API Key</Label>
          <div className="relative">
            <Input
              id="api_key"
              type={showKey ? 'text' : 'password'}
              placeholder={currentSettings.configured ? 'Leave blank to keep existing key' : 'Enter your Google Maps API Key'}
              value={settings.api_key}
              onChange={(e) => setSettings(prev => ({ ...prev, api_key: e.target.value }))}
            />
            <button
              type="button"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              onClick={() => setShowKey(!showKey)}
            >
              {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          {currentSettings.configured && currentSettings.api_key_masked && (
            <p className="text-xs text-gray-500">
              Current key: {currentSettings.api_key_masked}
            </p>
          )}
        </div>

        {/* Required APIs Info */}
        <div className="p-4 bg-gray-50 rounded-lg space-y-2">
          <Label className="text-sm font-medium">Required Google APIs</Label>
          <p className="text-xs text-gray-500">Make sure these APIs are enabled in your Google Cloud project:</p>
          <ul className="text-xs text-gray-600 list-disc list-inside space-y-1">
            <li>Places API (for address autocomplete)</li>
            <li>Maps JavaScript API (for map display)</li>
            <li>Geocoding API (for address validation)</li>
          </ul>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 pt-4">
          <Button 
            onClick={handleSave} 
            disabled={saving}
            className="bg-teal-600 hover:bg-teal-700"
          >
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
          
          {currentSettings.configured && (
            <Button 
              variant="outline" 
              onClick={handleDelete}
              disabled={saving}
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete Configuration
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default GoogleMapsSettings;
